import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCompanyStore } from "@/lib/store";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Instagram, Linkedin, Twitter } from "lucide-react";

const siteSettingsSchema = z.object({
  instagramUrl: z.string().url("Please enter a valid URL").or(z.string().length(0)),
  twitterUrl: z.string().url("Please enter a valid URL").or(z.string().length(0)),
  linkedinUrl: z.string().url("Please enter a valid URL").or(z.string().length(0)),
});

type FormValues = z.infer<typeof siteSettingsSchema>;

const SiteSettingsForm = () => {
  const { siteSettings, updateSiteSettings } = useCompanyStore();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(siteSettingsSchema),
    defaultValues: {
      instagramUrl: siteSettings?.instagramUrl || "",
      twitterUrl: siteSettings?.twitterUrl || "",
      linkedinUrl: siteSettings?.linkedinUrl || "",
    },
  });

  useEffect(() => {
    form.reset({
      instagramUrl: siteSettings?.instagramUrl || "",
      twitterUrl: siteSettings?.twitterUrl || "",
      linkedinUrl: siteSettings?.linkedinUrl || "",
    });
  }, [siteSettings, form]);

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    
    try {
      const success = await updateSiteSettings(data);
      
      if (success) {
        toast({
          title: "Settings updated",
          description: "Social media links have been updated successfully",
        });
      } else {
        toast({
          title: "Update failed",
          description: "There was an error updating the settings",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      toast({
        title: "Update failed",
        description: "There was an error updating the settings",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="instagramUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Instagram className="w-4 h-4" />
                  Instagram URL
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://instagram.com/your-handle"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="twitterUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Twitter className="w-4 h-4" />
                  Twitter URL
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://twitter.com/your-handle"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="linkedinUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4" />
                  LinkedIn URL
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://linkedin.com/company/your-company"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          type="submit"
          className="bg-redflags-red hover:bg-redflags-red/90 text-white"
          disabled={isLoading}
        >
          {isLoading ? "Saving..." : "Save Settings"}
        </Button>
      </form>
    </Form>
  );
};

export default SiteSettingsForm;
